import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@src/config/firebaseAuth';
import { useAtomValue } from 'jotai';
import { loginSubmittingAtom } from '@src/state/loginAtoms';

type AuthStatus = {
  isReady: boolean;
  isAuthenticated: boolean;
};

let authStatus: AuthStatus = {
  isReady: Boolean(auth.currentUser),
  isAuthenticated: Boolean(auth.currentUser),
};

const listeners = new Set<(next: AuthStatus) => void>();
let hasAuthListener = false;

const notify = (next: AuthStatus) => {
  // Fan out auth status updates to active hook subscribers.
  listeners.forEach((listener) => listener(next));
};

const ensureAuthListener = () => {
  // Register a single Firebase auth listener and broadcast changes.
  if (hasAuthListener) {
    return;
  }
  hasAuthListener = true;
  onAuthStateChanged(auth, (user) => {
    authStatus = {
      isReady: true,
      isAuthenticated: Boolean(user),
    };
    notify(authStatus);
  });
};

export function useAuthStatus(): AuthStatus {
  /**
   * Subscribe to a shared Firebase auth listener and expose a stable status
   * so hooks can gate queries without registering multiple listeners.
   */
  const [state, setState] = useState<AuthStatus>(authStatus);

  useEffect(() => {
    ensureAuthListener();
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}

export function useAuthEnabled(): boolean {
  /**
   * Convenience hook to gate queries once auth is ready and authenticated.
   */
  const { isReady, isAuthenticated } = useAuthStatus();
  const isLoginSubmitting = useAtomValue(loginSubmittingAtom);
  return isReady && isAuthenticated && !isLoginSubmitting;
}

export const resetAuthStatusForTests = (user: unknown = auth.currentUser) => {
  // Test-only helper to reset auth cache and listeners between runs.
  authStatus = {
    isReady: Boolean(user),
    isAuthenticated: Boolean(user),
  };
  listeners.clear();
  hasAuthListener = false;
};
